import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import TierBadge from '../components/TierBadge.jsx'
import ProposeRanges from '../trips/ProposeRanges.jsx'
import {
  answeredLine,
  avatarToken,
  barWidth,
  countLabel,
  deadlineLabel,
  formatRange,
  gapLabel,
  hardestToFit,
  missingPhrase,
  rankLabel,
  sendLabel,
  sentSummary,
  subLine,
  verdictBody,
  verdictHeadline,
} from '../trips/dates.js'
import {
  confirmRange,
  loadDates,
  proposeRanges,
  sendSelection,
  undoConfirmation,
} from '../trips/api.js'
import { openLiveChannel } from '../trips/live.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// The Trip Dates screen — P5-3 T6, ruling TR8.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 MOBILE-FIRST, AND THE DESIGN BRIEF SAYS THIS ONE IS NOT NEGOTIABLE
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF: *"Member view — phone ... Tap every one that works. Nothing's
// booked."* And the reason, which is the actual requirement: availability
// answers happen on phones, half-attentive, and *an accurate poll nobody
// finishes is worse than a rough one everyone does.*
//
// So the answer path is: open, tap the rows, tap Send. No calendar picker, no
// per-range drawer, no confirm dialog, and the escape hatch lives in the
// sticky footer next to Send *specifically* so it stays reachable at ten
// ranges as well as three. Every one of those is a place somebody quits.
//
// The rows are 60px and the footer controls are 44px and 52px, which is the
// mock — and above this system's own `--d2d-hit-min` floor, which is what the
// CSS actually asserts.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NOTHING ON THIS SCREEN PICKS A WINNER
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF's non-negotiable, and the reason several obvious "improvements" are
// absent rather than forgotten: every row carries the SAME neutral "Confirm
// this"; top rows differ by background, rank colour and bar fill only, never by
// a badge or a different label; ties render as ties; and when no range works
// for everyone the verdict says so in plain words at the top instead of
// quietly ranking around it.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 LIVENESS IS THE BOARD'S, UNCHANGED — NO SECOND MECHANISM
// ═══════════════════════════════════════════════════════════════════════════
//
// Spec §5.6: *"The live channel **is** a tail of this feed. The fallback **is**
// a poll of this feed ... No second mechanism for any of the three, ever."*
//
// Every poll mutation appends a `change` row through the service's `mutation()`
// seal, so it rides down the tail that already exists. `src/trips/live.js` is
// untouched by this slice; this component opens the same channel the board
// does, with the cursor its Function took BEFORE reading the poll, and treats a
// change as *"re-read"* rather than as a patch — same argument as the board:
// a change moves the ranking, the ranks, the ties and the verdict, none of it
// derivable here without reimplementing `utils/ranges.py`.

const EMPTY = []

export default function TripDates({ section }) {
  const { tripId } = useParams()
  useDocumentTitle('Dates')

  const [state, setState] = useState({ status: 'loading', data: null, error: null })
  const [picks, setPicks] = useState(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [changing, setChanging] = useState(false)

  // Every read is stamped and only the newest is applied — the board's rule,
  // and for the board's reason: a live re-read started before this member's own
  // Send and answered after it would put the pre-send poll back on screen, and
  // they would watch their own answer disappear.
  const generation = useRef(0)

  const refresh = useCallback(
    async ({ background = false } = {}) => {
      const mine = generation.current + 1
      generation.current = mine
      try {
        const data = await loadDates(tripId)
        if (generation.current !== mine) return true
        setState({ status: 'ready', data, error: null })
        return true
      } catch (error) {
        if (generation.current !== mine) return false
        // A refresh nobody asked for never blanks a working screen (§8: the
        // fallback engages "with no user-visible ceremony", and a page that
        // replaces itself with an error notice is the largest ceremony there
        // is).
        if (background) return false
        setState({ status: 'error', data: null, error: error.message })
        return false
      }
    },
    [tripId],
  )

  const refreshRef = useRef(refresh)
  refreshRef.current = refresh

  useEffect(() => {
    let cancelled = false
    let channel = null
    loadDates(tripId).then(
      (data) => {
        if (cancelled) return
        generation.current += 1
        setState({ status: 'ready', data, error: null })
        channel = openLiveChannel({
          tripId,
          cursor: data.cursor,
          onChange: () => refreshRef.current({ background: true }),
        })
      },
      (error) => {
        if (!cancelled) setState({ status: 'error', data: null, error: error.message })
      },
    )
    return () => {
      cancelled = true
      // 🔴 Closed on unmount, always. A leaked channel makes no requests while
      // it is quiet, so "no new requests after unmount" is a test that passes
      // either way — T5 recorded exactly that trap. The close is what stops a
      // second channel opening on every navigation back to this page.
      channel?.close()
    }
  }, [tripId])

  const run = useCallback(
    async (work) => {
      setBusy(true)
      setActionError(null)
      try {
        await work()
        await refresh()
        return true
      } catch (error) {
        setActionError(error.message)
        return false
      } finally {
        setBusy(false)
      }
    },
    [refresh],
  )

  // 🔴 THE HEADING IS RENDERED IN EVERY STATE, INCLUDING WHILE LOADING AND ON
  // FAILURE. Two reasons, and the second is the one that was found rather than
  // reasoned about: this site's rule is one h1 per page, and a page whose
  // heading only appears once a fetch resolves has none at all for a screen
  // reader while it does not; and a test that asks "which page did the router
  // choose?" has nothing to read on the failure path, which is exactly the
  // question CLAUDE.md gotcha 21 says to put to the real router.
  const header = (title) => (
    <header className="d2d-titleblock">
      <p className="d2d-eyebrow d2d-micro">
        <TierBadge tier={section?.tier ?? 3} />
      </p>
      <p className="d2d-board__back">
        <Link to={`/trips/${tripId}`}>← {title ?? 'the trip'}</Link>
      </p>
      <h1 className="d2d-titleblock__headline">When can everyone actually go?</h1>
    </header>
  )

  if (state.status === 'loading') {
    return (
      <article className="d2d-page d2d-dates">
        {header()}
        <hr className="d2d-rule" />
        <p className="d2d-notice" data-testid="dates-loading">
          Loading the dates.
        </p>
      </article>
    )
  }

  if (state.status === 'error') {
    return (
      <article className="d2d-page d2d-dates">
        {header()}
        <hr className="d2d-rule" />
        <p className="d2d-notice" data-testid="dates-error">
          {state.error}
        </p>
      </article>
    )
  }

  const { poll, trip, you } = state.data
  const organiser = (poll ? poll.your_role : trip.your_role) === 'organiser'

  if (!poll) {
    return (
      <article className="d2d-page d2d-dates">
        {header(trip.title)}
        <hr className="d2d-rule" />
        <p className="d2d-notice" data-testid="dates-empty">
          {organiser
            ? 'No dates proposed yet. Put up to ten weeks up and everyone taps the ones that work.'
            : 'No dates proposed yet. Whoever started this trip puts the weeks up.'}
        </p>
        {organiser ? (
          <ProposeRanges
            busy={busy}
            onPropose={(body) => run(() => proposeRanges(tripId, body))}
          />
        ) : null}
        {actionError ? (
          <p className="d2d-notice d2d-notice--warn" role="status" data-testid="dates-action-error">
            {actionError}
          </p>
        ) : null}
      </article>
    )
  }

  const closed = poll.state !== 'dates_polling'
  const sent = poll.your_selection !== null
  const selected = picks ?? poll.your_selection ?? EMPTY
  const deadline = deadlineLabel(poll.deadline)
  const answering = !closed && (!sent || changing)

  const toggle = (rangeId) => {
    setPicks((current) => {
      const base = current ?? poll.your_selection ?? EMPTY
      return base.includes(rangeId)
        ? base.filter((id) => id !== rangeId)
        : [...base, rangeId]
    })
  }

  const send = async () => {
    const ok = await run(() => sendSelection(tripId, selected))
    if (ok) {
      setPicks(null)
      setChanging(false)
    }
  }

  return (
    <article className="d2d-page d2d-dates">
      {header(trip.title)}
      <hr className="d2d-rule" />

      {actionError ? (
        <p className="d2d-notice d2d-notice--warn" role="status" data-testid="dates-action-error">
          {actionError}
        </p>
      ) : null}

      {/* ── the member's answer ─────────────────────────────────────────── */}
      <section className="d2d-dates__answer" aria-label="Which of these could you make?">
        {answering ? (
          <>
            <h2 className="d2d-dates__h2">Which of these could you make?</h2>
            <p className="d2d-dates__lede">
              Tap every one that works. Nothing is booked — the organiser picks from your
              answers.
            </p>
            {deadline ? <p className="d2d-micro d2d-dates__deadline">{deadline}</p> : null}

            <ul className="d2d-dates__rows">
              {poll.candidate_ranges.map((range) => {
                const on = selected.includes(range.id)
                return (
                  <li key={range.id}>
                    <button
                      type="button"
                      className={`d2d-daterow${on ? ' d2d-daterow--on' : ''}`}
                      aria-pressed={on}
                      disabled={busy}
                      data-testid={`range-${range.id}`}
                      onClick={() => toggle(range.id)}
                    >
                      <span className="d2d-daterow__text">
                        <span className="d2d-daterow__label">
                          {formatRange(range.start, range.end)}
                        </span>
                        <span className="d2d-daterow__sub">{subLine(range)}</span>
                      </span>
                      <span className="d2d-daterow__tick" aria-hidden="true">
                        {on ? '✓' : ''}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>

            {/* 🔴 The escape hatch is in the footer BESIDE Send, not at the
                bottom of the list — HANDOFF is explicit that it lives there so
                it stays reachable at ten ranges. It is a real answer: it clears
                the picks and sends an empty list, which the service stores as
                "answered: none of these", distinct from never having answered. */}
            <div className="d2d-dates__footer">
              <button
                type="button"
                className={`d2d-dates__none${selected.length === 0 ? ' d2d-dates__none--on' : ''}`}
                disabled={busy}
                data-testid="dates-none"
                onClick={() => setPicks(EMPTY)}
              >
                None of these work for me
              </button>
              <button
                type="button"
                className="d2d-dates__send"
                disabled={busy}
                data-testid="dates-send"
                onClick={send}
              >
                {busy ? 'Sending…' : sendLabel(selected, poll.candidate_ranges.length)}
              </button>
              <p className="d2d-dates__footnote">
                You can change this until the organiser confirms.
              </p>
            </div>
          </>
        ) : (
          <div data-testid="dates-sent">
            {sent ? (
              <>
                <p className="d2d-dates__chip d2d-micro">Answer sent</p>
                <h2 className="d2d-dates__h2">
                  {sentSummary(poll.your_selection, poll.candidate_ranges.length)}
                </h2>
                <ul className="d2d-dates__sentlist">
                  {poll.candidate_ranges.map((range) => {
                    const on = poll.your_selection.includes(range.id)
                    return (
                      <li key={range.id} className={on ? '' : 'd2d-dates__sentlist--off'}>
                        <span className="d2d-dates__mark" aria-hidden="true">
                          {on ? '✓' : '—'}
                        </span>
                        <span>{formatRange(range.start, range.end)}</span>
                        <span className="d2d-sr-only">
                          {on ? ' works for you' : ' does not work for you'}
                        </span>
                      </li>
                    )
                  })}
                </ul>
                {!closed ? (
                  <button
                    type="button"
                    className="d2d-dates__change"
                    disabled={busy}
                    data-testid="dates-change"
                    onClick={() => {
                      setPicks(poll.your_selection)
                      setChanging(true)
                    }}
                  >
                    Change my answer
                  </button>
                ) : null}
              </>
            ) : (
              <p className="d2d-notice" data-testid="dates-closed">
                The dates are confirmed, so the poll is closed.
              </p>
            )}
          </div>
        )}
      </section>

      {/* ── the results ─────────────────────────────────────────────────── */}
      <section className="d2d-dates__results" aria-label="What everyone said">
        <div className="d2d-verdict">
          <h2 className="d2d-verdict__headline" data-testid="verdict-headline">
            {verdictHeadline(poll)}
          </h2>
          <p className="d2d-verdict__body" data-testid="verdict-body">
            {verdictBody(poll, you)}
          </p>
          <ul className="d2d-verdict__meta">
            <li data-testid="answered-line">{answeredLine(poll)}</li>
            <li>
              {poll.candidate_ranges.length} ranges proposed · 10 is the cap
            </li>
            {deadline ? <li>{deadline}</li> : null}
          </ul>
          {/* HANDOFF's amber footnote, verbatim in substance: the tool does not
              decide, and it says so on the screen rather than only in a spec. */}
          <p className="d2d-verdict__rule d2d-micro" data-testid="never-picks">
            The poll never picks · you confirm
          </p>
        </div>

        <ul className="d2d-results">
          {poll.results.map((row) => (
            <li
              key={row.range_id}
              className={`d2d-result${row.is_top ? ' d2d-result--top' : ''}`}
              data-testid={`result-${row.range_id}`}
            >
              <div className="d2d-result__main">
                <p className="d2d-result__head">
                  <span className="d2d-result__rank d2d-micro" data-testid={`rank-${row.range_id}`}>
                    {rankLabel(row)}
                  </span>
                  <span className="d2d-result__label">{formatRange(row.start, row.end)}</span>
                  <span className="d2d-result__sub">{subLine(row)}</span>
                </p>
                <p className="d2d-result__people">
                  <span className="d2d-avatars" aria-hidden="true">
                    {row.available.map((userId) => (
                      <span
                        key={userId}
                        className="d2d-avatar"
                        style={{ background: `var(${avatarToken(userId)})` }}
                      />
                    ))}
                  </span>
                  <span
                    className={
                      (row.unavailable ?? []).length === 0
                        ? 'd2d-result__miss d2d-result__miss--none'
                        : 'd2d-result__miss'
                    }
                    data-testid={`miss-${row.range_id}`}
                  >
                    {missingPhrase(row, you)}
                  </span>
                </p>
              </div>

              <div className="d2d-result__meta">
                <p className="d2d-result__counts">
                  <span className="d2d-micro">{countLabel(row, poll)}</span>
                  <span className="d2d-micro d2d-result__gap">{gapLabel(row)}</span>
                </p>
                <span className="d2d-result__bar" aria-hidden="true">
                  <span className="d2d-result__fill" style={{ width: barWidth(row, poll) }} />
                </span>
              </div>

              {/* 🔴 IDENTICAL ON EVERY ROW. Same words, same class, same size —
                  HANDOFF: "only ever offer a neutral, identical 'Confirm this'
                  on each." The top rows differ by background and accent, never
                  by a label. */}
              {organiser && !closed ? (
                <button
                  type="button"
                  className="d2d-result__confirm"
                  disabled={busy}
                  data-testid={`confirm-${row.range_id}`}
                  onClick={() => run(() => confirmRange(tripId, row.range_id))}
                >
                  Confirm this
                </button>
              ) : null}
            </li>
          ))}
        </ul>

        {poll.confirmed_range_id ? (
          <div className="d2d-confirmed" data-testid="confirmed-band">
            <p className="d2d-confirmed__text">
              <span className="d2d-micro d2d-confirmed__eyebrow">Confirmed</span>
              {formatRange(poll.confirmed_range_start, poll.confirmed_range_end)}
              {'. '}
              {(() => {
                const row = poll.results.find((each) => each.range_id === poll.confirmed_range_id)
                const missing = row ? (row.unavailable ?? []).length : 0
                return missing === 0
                  ? 'Everyone who answered can make it.'
                  : `${missing} of you cannot make it, and ${
                      missing === 1 ? 'has' : 'have'
                    } to be told directly rather than left to find out.`
              })()}
            </p>
            {organiser ? (
              <button
                type="button"
                className="d2d-confirmed__undo"
                disabled={busy}
                data-testid="undo-confirm"
                onClick={() => run(() => undoConfirmation(tripId))}
              >
                Undo
              </button>
            ) : null}
          </div>
        ) : null}

        {poll.respondents.length > 0 ? (
          <div className="d2d-hardest" data-testid="hardest-to-fit">
            <p className="d2d-micro d2d-hardest__eyebrow">Who is hardest to fit</p>
            <ul className="d2d-hardest__list">
              {hardestToFit(poll).map((person) => (
                <li key={person.userId} className="d2d-hardest__row">
                  <span
                    className="d2d-avatar"
                    aria-hidden="true"
                    style={{ background: `var(${avatarToken(person.userId)})` }}
                  />
                  <span className="d2d-hardest__who">
                    {person.userId === you ? 'you' : 'someone on the trip'}
                  </span>
                  <span className="d2d-micro d2d-hardest__score">
                    {person.count === null
                      ? 'has not answered'
                      : `${person.count} of ${person.total}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {organiser && !closed ? (
          <ProposeRanges
            busy={busy}
            existing={poll.candidate_ranges}
            answered={poll.respondents.length}
            onPropose={(body) => run(() => proposeRanges(tripId, body))}
          />
        ) : null}
      </section>
    </article>
  )
}
